import { useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { Toast } from '@repo/videa-components/src/index';
import { cn } from '@repo/videa-components/src/lib/utils';
import { Button } from '@repo/videa-components/src/ui/button';
import { Input } from '@repo/videa-components/src/ui/input';
import { toast } from 'sonner';

import {
  usePreferences,
  usePreferencesPracticeUpdate,
} from '@/react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

function isValidHttpUrl(value: string): boolean {
  if (!value) return true;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export const QRCodeBookingUrlInput = () => {
  const { data: prefs } = usePreferences();
  const { t } = useLingui();
  const [tempValue, setTempValue] = useState<string>(
    prefs?.qrCodeBookingUrl ?? '',
  );

  const { trackEvent } = useTrackEvent();
  const preferencesUpdate = usePreferencesPracticeUpdate();
  const isInvalid = !isValidHttpUrl(tempValue);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempValue(e.target.value);
  };

  const handleSave = () => {
    if (isInvalid) return;

    preferencesUpdate.mutate(
      { qrCodeBookingUrl: tempValue },
      {
        onSuccess: () => {
          trackEvent(mixpanelEvents.qrBookingUrlSaved, {
            bookingUrl: tempValue,
          });
          toast(
            <Toast
              variant="success"
              title={t`Booking URL saved successfully!`}
            />,
            {
              closeButton: true,
              position: 'top-right',
              style: { width: '460px' },
            },
          );
        },
        onError: () => {
          toast(
            <Toast
              variant="error"
              title={t`Failed to save booking URL`}
              description={t`Please try again or contact support if the problem persists.`}
            />,
            {
              closeButton: true,
              position: 'top-right',
              style: { width: '460px' },
            },
          );
        },
      },
    );
  };

  return (
    <div className="vai-flex vai-flex-col vai-gap-1 vai-w-full">
      <p className="vai-typography-small !vai-mb-0">
        <Trans>QR Code Booking URL</Trans>
      </p>
      <p className="vai-text-sm vai-text-zinc-500 !vai-mb-2">
        <Trans>
          If set, a &quot;Book Now&quot; link will appear on the patient QR code
          document.
        </Trans>
      </p>
      <div className="vai-flex vai-items-center vai-gap-2">
        <Input
          id="qr-booking-url"
          type="url"
          placeholder="https://example.com/book"
          value={tempValue}
          onChange={handleChange}
          aria-invalid={isInvalid}
          className={cn(
            'vai-flex-1 [&:focus-visible]:vai-ring-0 [&:focus-visible]:vai-ring-offset-0 [&:focus-visible]:vai-outline-none',
            isInvalid && 'vai-border-red-500',
          )}
          disabled={preferencesUpdate.isLoading}
        />
        <Button
          size="sm"
          onClick={handleSave}
          className="vai-h-8"
          disabled={isInvalid || preferencesUpdate.isLoading}
        >
          {preferencesUpdate.isLoading ? (
            <Trans>Saving...</Trans>
          ) : (
            <Trans>Save</Trans>
          )}
        </Button>
      </div>
      {isInvalid && (
        <div className="vai-text-red-500 vai-text-sm" role="alert">
          <Trans>Please enter a valid URL starting with http:// or https://</Trans>
        </div>
      )}
    </div>
  );
};
